import React, {useState, useEffect, useRef} from 'react';
import { v4 as uuidv4 } from 'uuid';

export function Autocomplete({placeholder, buildUrl, transformSuggestionsResponse, displaySuggestion, fieldName, allowMultipleSelection, minChars}) {

    const [searchInput, setSearchInput] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSuggestionsHidden, setIsSuggestionsHidden] = useState(true);
    const [optionIndex, setOptionIndex] = useState(-1);
    const [inputCleared, setInputCleared] = useState(false);

    const searchInputRef = useRef(null);
    const suggestionsRef = useRef([]);

    useEffect(() => {
        if (inputCleared) {
            searchInputRef.current.focus();
            setInputCleared(false)
        }
        if (searchInput.length > 0) {
            const timeoutRef = setTimeout(() => {
                setLoading(true);
                setSuggestions([]);
                const url = buildUrl(searchInput);
                fetch(url)
                    .then(res => res.json())
                    .then(json => {
                        setLoading(false);
                        setSuggestions(transformSuggestionsResponse(json));
                        setIsSuggestionsHidden(false);
                        setOptionIndex(-1);
                    })
                    .catch(err => {
                        console.error(err);
                        setLoading(false);
                        setSuggestions([]);
                        setIsSuggestionsHidden(true);
                        setOptionIndex(-1);
                    });
            }, 300)
            return () => clearTimeout(timeoutRef);
        }
    }, [searchInput]);

    useEffect(() => {
        if (!isSuggestionsHidden) {
            if (searchInput.length >= minChars) {
                const timeoutRef = setTimeout(() => {
                    setLoading(true);
                    const url = buildUrl(searchInput);
                    fetch(url)
                        .then(res => res.json())
                        .then(json => {
                            setLoading(false);
                            setSuggestions(transformSuggestionsResponse(json));
                            setIsSuggestionsHidden(false);
                            setOptionIndex(-1);
                        })
                        .catch(err => {
                            console.error(err);
                            setLoading(false);
                            setSuggestions([]);
                            setIsSuggestionsHidden(true);
                            setOptionIndex(-1);
                        });
                }, 300)
                return () => clearTimeout(timeoutRef);
            }
        }
    }, [isSuggestionsHidden]);

    const selectedSuggestion = (suggestion) => {
        setSearchInput('');
        let newSelected;
        if (allowMultipleSelection) {
            newSelected = selected.find(s => s.properties.citycode === suggestion.properties.citycode) ? selected : [...selected, suggestion];
        } else {
            newSelected = [suggestion];
        }
        setSelected(newSelected);
        setIsSuggestionsHidden(true);
        resetInput();
    }
    
    const resetHandler = () => {
        let newSelected = [];
        setSelected(newSelected);
        setInputCleared(true);
        resetInput();
    };

    const resetInput = () => {
        setOptionIndex(-1);
        setSearchInput('');
        setSuggestions([]);
        setIsSuggestionsHidden(true);
    };
    
    const keyDownHandler = (event) => {
        switch (event.key) {
            case 'ArrowUp':
                let newOptionIndex = optionIndex <= 0 ? suggestions.length - 1 : optionIndex - 1;
                setOptionIndex(newOptionIndex);
                if (suggestionsRef.current[newOptionIndex]) {
                    suggestionsRef.current[newOptionIndex].scrollIntoView({behavior: 'auto', block: 'nearest'});
                    suggestionsRef.current[newOptionIndex].focus();
                }
                break;
            case 'ArrowDown':
                if (event.altKey) {
                    setIsSuggestionsHidden(false);
                } else {
                    setIsSuggestionsHidden(false);
                    let newOptionIndex = (isSuggestionsHidden || optionIndex === suggestions.length - 1) ? 0 : optionIndex + 1;
                    setOptionIndex(newOptionIndex);
                    if (suggestionsRef.current[newOptionIndex]) {
                        suggestionsRef.current[newOptionIndex].scrollIntoView({behavior: 'auto', block: 'nearest'});
                        suggestionsRef.current[newOptionIndex].focus();
                    }
                }
                break;
            case 'Escape':
                if (isSuggestionsHidden) setSearchInput('');
                searchInputRef.current.focus();
                setIsSuggestionsHidden(true);
                break;
            case 'Tab':
                setIsSuggestionsHidden(true);
                break;
            case 'Enter':
                if (suggestions[optionIndex]) selectedSuggestion(suggestions[optionIndex]);
                searchInputRef.current.focus();
                setIsSuggestionsHidden(true);
                break;
            default:
                break;
        }
    };


    return (
        <div className="form-group">
            <div className="field-item champ-autocomplete">
                <label htmlFor="field-autocomplete-commune">
                    Recherchez votre commune
                </label>
                {selected.length > 0 && <ul className="selected">
                    {selected.map(s => <li>{displaySuggestion(s)}</li>)}
                </ul>}
                <input type="text" inputMode="none" className="visually-hidden" name={fieldName} value={selected.map(s => s.properties.citycode).join(',')} required/>
                <input id="field-autocomplete-commune"
                       className="form-control form-control-lg"
                       ref={searchInputRef}
                       name={uuidv4()}
                       type="text"
                       autoComplete="off"
                       aria-autocomplete="list"
                       role="combobox"
                       aria-owns="liste-options-field-autocomplete"
                       aria-describedby="field-autocomplete-commune-hint"
                       title={placeholder}
                       disabled={!allowMultipleSelection && selected.length > 0}
                       placeholder={placeholder}
                       value={searchInput}
                       onFocus={() => setIsSuggestionsHidden(false)}
                       onChange={(e) => setSearchInput(e.target.value)}
                       onKeyDown={keyDownHandler}/>
                {searchInput.length > 0 && loading &&
                    <div className="loader-wrap">
                        <div className="loader" title="Chargement des résultats en cours"></div>
                    </div>
                }
                <ul className={`dropdown-menu ${(isSuggestionsHidden || searchInput.length  < minChars || suggestions.length === 0) ? '' : 'show'}`}
                    id="liste-options-field-autocomplete"
                    role="listbox"
                    aria-label="Suggestions de saisie">
                    {suggestions.map((suggestion, index) => {
                        return (<li className="dropdown-item"
                            ref={(ref) => suggestionsRef.current[index] = ref}
                            role="option"
                            tabIndex="-1"
                            aria-selected={optionIndex === index}
                            id={"field-autocomplete-" + index}
                            onKeyDown={keyDownHandler}
                            onClick={() => selectedSuggestion(suggestion)}>
                            <span>{displaySuggestion(suggestion)}</span>
                        </li>);
                    })}
                </ul>
                <div className="visually-hidden" aria-live="polite" aria-atomic="true">
                    <p>{suggestions.length} résultats disponibles.</p>
                </div>
                {selected.length > 0 && !loading &&
                <button className="reset"
                        type="button"
                        title="Effacer le contenu"
                        onClick={resetHandler}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                    <div className="visually-hidden">Effacer le contenu</div>
                </button>
                }
            </div>
        </div>
    )
}